export const POMPEI_STATIC_CACHE = 'pompei:public-static-v4';

// Run before mounting Vue: an old worker can otherwise satisfy the first map
// request from an authenticated API cache while its replacement is installing.
export async function preparePublicServiceWorker({ browser = window, workerUrl = '/public-service-worker.js', expectedCache = POMPEI_STATIC_CACHE } = {}) {
    try {
        if (browser.caches) {
            const keys = await browser.caches.keys();
            await Promise.all(keys.filter(key => key !== expectedCache).map(key => browser.caches.delete(key)));
        }
        const serviceWorker = browser.navigator?.serviceWorker;
        if (!serviceWorker) return true;
        const registration = await serviceWorker.register(workerUrl, { scope: '/', updateViaCache: 'none' });
        await registration.update();
        const nextWorker = registration.installing ?? registration.waiting;
        if (nextWorker && nextWorker.state !== 'activated') {
            nextWorker.postMessage('SKIP_WAITING');
            await new Promise((resolve, reject) => {
                const timer = browser.setTimeout(() => reject(new Error('Service worker activation timed out')), 5000);
                const inspect = () => {
                    if (nextWorker.state === 'activated' || nextWorker.state === 'redundant') {
                        browser.clearTimeout(timer);
                        nextWorker.removeEventListener('statechange', inspect);
                        nextWorker.state === 'activated' ? resolve() : reject(new Error('Service worker activation failed'));
                    }
                };
                nextWorker.addEventListener('statechange', inspect);
                inspect();
            });
        }
        const active = registration.active;
        if (!active) return !serviceWorker.controller;
        const safe = await new Promise(resolve => {
            const channel = new browser.MessageChannel();
            const timer = browser.setTimeout(() => { channel.port1.close(); resolve(false); }, 2000);
            channel.port1.onmessage = event => {
                browser.clearTimeout(timer);
                channel.port1.close();
                resolve(event.data === expectedCache);
            };
            active.postMessage('PUBLIC_CACHE_POLICY', [channel.port2]);
        });
        if (!safe) return false;
        // clients.claim runs during activation. A previous worker must not keep
        // control when interactive requests start.
        if (serviceWorker.controller && serviceWorker.controller !== active) {
            return await new Promise(resolve => {
                const timer = browser.setTimeout(() => { serviceWorker.removeEventListener('controllerchange', changed); resolve(false); }, 2000);
                const changed = () => {
                    browser.clearTimeout(timer);
                    serviceWorker.removeEventListener('controllerchange', changed);
                    resolve(serviceWorker.controller === active);
                };
                serviceWorker.addEventListener('controllerchange', changed);
            });
        }
        return true;
    } catch {
        // Preserve the server-rendered, navigable document when a legacy worker
        // cannot be replaced. Do not initiate potentially cached API requests.
        return !browser.navigator?.serviceWorker?.controller;
    }
}

export const preparePompeiServiceWorker = preparePublicServiceWorker;
